import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Typography, 
  Card, 
  Button, 
  Space, 
  Table, 
  Modal, 
  Progress, 
  Tag, 
  Descriptions, 
  App,
  Alert,
  Spin
} from 'antd';
import { 
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;

const DocumentReview = () => {
  const { message } = App.useApp();
  const { documentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [reviewTasks, setReviewTasks] = useState([]);
  const [currentTask, setCurrentTask] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportContent, setReportContent] = useState('');
  const [canReview, setCanReview] = useState(false);
  const [reviewReason, setReviewReason] = useState('');
  const [documentStatus, setDocumentStatus] = useState('');
  const [polling, setPolling] = useState(false);

  const loadReviewTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/documents/review-tasks?document_id=${documentId}`);
      setReviewTasks(response.data.tasks);
      
      if (response.data.tasks.length > 0) {
        const latestTask = response.data.tasks[0];
        setCurrentTask(latestTask);
        
        if (latestTask.status === 'processing' || latestTask.status === 'summarizing' || latestTask.status === 'finalizing') {
          loadTaskStatus(latestTask.id);
        }
      }
    } catch (error) {
      message.error('加载评审任务失败');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  const loadTaskStatus = useCallback(async (taskId) => {
    try {
      const response = await api.get(`/documents/review-tasks/${taskId}/status`);
      setTaskStatus(response.data);
      
      if (response.data.status === 'completed' || response.data.status === 'failed') {
        setPolling(false);
        loadReviewTasks();
      }
    } catch (error) {
      message.error('加载任务状态失败');
    }
  }, []);

  const checkCanReview = useCallback(async () => {
    try {
      const response = await api.get(`/documents/${documentId}/can-review`);
      setCanReview(response.data.can_review);
      setReviewReason(response.data.reason || '');
      setDocumentStatus(response.data.current_status);
    } catch (error) {
      message.error('检查评审权限失败');
    }
  }, [documentId]);

  useEffect(() => {
    loadReviewTasks();
    checkCanReview();
  }, [loadReviewTasks, checkCanReview]);

  useEffect(() => {
    let intervalId = null;
    
    if (polling && currentTask) {
      intervalId = setInterval(() => {
        loadTaskStatus(currentTask.id);
      }, 3000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [polling, currentTask, loadTaskStatus]);

  const handleStartReview = async () => {
    try {
      const response = await api.post(`/documents/${documentId}/review`);
      message.success('评审任务已创建');
      setCanReview(false);
      setReviewReason('文档正在评审中');
      setPolling(true);
      loadReviewTasks();
    } catch (error) {
      message.error(error.response?.data?.error || '创建评审任务失败');
    }
  };

  const handleDownloadReport = async (taskId) => {
    try {
      const response = await api.get(`/documents/review-tasks/${taskId}/report`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `评审报告_${taskId}.md`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('报告下载成功');
    } catch (error) {
      message.error('下载报告失败');
    }
  };

  const handleViewReport = async (taskId) => {
    try {
      const response = await api.get(`/documents/review-tasks/${taskId}/report`, {
        responseType: 'text'
      });
      setReportContent(response.data);
      setReportModalVisible(true);
    } catch (error) {
      message.error('加载报告失败');
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'pending': { color: 'default', text: '待处理', icon: <ClockCircleOutlined /> },
      'processing': { color: 'processing', text: '处理中', icon: <Spin size="small" /> },
      'summarizing': { color: 'processing', text: '汇总中', icon: <Spin size="small" /> },
      'finalizing': { color: 'processing', text: '生成报告中', icon: <Spin size="small" /> },
      'completed': { color: 'success', text: '已完成', icon: <CheckCircleOutlined /> },
      'failed': { color: 'error', text: '失败', icon: <ExclamationCircleOutlined /> }
    };
    const { color, text, icon } = statusMap[status] || { color: 'default', text: status, icon: null };
    return <Tag color={color} icon={icon}>{text}</Tag>;
  };

  const getStageText = (stage) => {
    const stageMap = {
      'pending': '待处理',
      'chunk_processing': '分片处理中',
      'summarizing': '结果汇总中',
      'finalizing': '生成报告中',
      'completed': '已完成'
    };
    return stageMap[stage] || stage;
  };

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'id',
      key: 'id',
      width: '10%',
    },
    {
      title: '文档标题',
      dataIndex: 'document_title',
      key: 'document_title',
      width: '30%',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: '12%',
      render: (status) => getStatusTag(status),
    },
    {
      title: '当前阶段',
      dataIndex: 'current_stage',
      key: 'current_stage',
      width: '15%',
      render: (stage) => <Text>{getStageText(stage)}</Text>,
    },
    {
      title: '进度',
      dataIndex: 'total_progress',
      key: 'total_progress',
      width: '15%',
      render: (progress) => (
        <Progress 
          percent={progress} 
          size="small" 
          status={progress === 100 ? 'success' : 'active'}
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '10%',
    },
    {
      title: '操作',
      key: 'action',
      width: '18%',
      render: (_, record) => (
        <Space>
          {record.status === 'completed' && (
            <>
              <Button 
                type="link" 
                size="small" 
                icon={<FileTextOutlined />}
                onClick={() => handleViewReport(record.id)}
              >
                查看
              </Button>
              <Button 
                type="link" 
                size="small" 
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadReport(record.id)}
              >
                下载
              </Button>
            </>
          )}
          {record.status === 'failed' && (
            <Button 
              type="link" 
              size="small" 
              icon={<ReloadOutlined />}
              onClick={() => handleStartReview()}
            >
              重试
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3}>文档评审</Title>
        <Text type="secondary">使用智能体对文档进行离线评审</Text>
      </div>

      {!canReview && reviewReason && (
        <Alert
          message={reviewReason}
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {currentTask && taskStatus && (taskStatus.status === 'processing' || taskStatus.status === 'summarizing' || taskStatus.status === 'finalizing') && (
        <Card style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>当前任务进度</Text>
              <Tag color="processing">{getStageText(taskStatus.current_stage)}</Tag>
            </div>
            <Progress 
              percent={taskStatus.total_progress} 
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <Space>
              <Text type="secondary">已处理: {taskStatus.processed_chunks}/{taskStatus.total_chunks} 个分片</Text>
              <Text type="secondary">阶段进度: {taskStatus.stage_progress}%</Text>
            </Space>
          </Space>
        </Card>
      )}

      <Card
        title="评审历史"
        extra={
          canReview && (
            <Button type="primary" onClick={handleStartReview}>
              发起评审
            </Button>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={reviewTasks}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个任务`,
          }}
        />
      </Card>

      <Modal
        title="评审报告"
        open={reportModalVisible}
        onCancel={() => setReportModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setReportModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="download" 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={() => currentTask && handleDownloadReport(currentTask.id)}
          >
            下载报告
          </Button>
        ]}
        width={800}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <Paragraph>
            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {reportContent}
            </pre>
          </Paragraph>
        </div>
      </Modal>
    </div>
  );
};

export default DocumentReview;